import { useLayoutEffect } from 'react';

import { css } from '@/styled-system/css';
import { MIN_LG } from '@/src/lib/breakpoints';
import { STATIC_MAP_CAMERA_VERSION, type StaticMapBounds } from '@/src/lib/utils/staticMap';

const STATIC_MAP_PREVIEW_PROPERTY = '--hotpads-static-map-preview-image';
const STATIC_MAP_PREVIEW_LOADED_DATASET_KEY = 'hotpadsStaticMapPreviewLoaded';
const STATIC_MAP_PREVIEW_ERROR_DATASET_KEY = 'hotpadsStaticMapPreviewError';
const STATIC_MAP_PREVIEW_URL_DATASET_KEY = 'hotpadsStaticMapPreviewUrl';
const STATIC_MAP_HEAD_BOOTSTRAP_DATASET_KEY = 'hotpadsStaticMapBootstrap';
const STATIC_MAP_PREVIEW_LOAD_EVENT = 'hotpads:static-map-preview-load';
const STATIC_MAP_PREVIEW_ERROR_EVENT = 'hotpads:static-map-preview-error';
const STATIC_MAP_LAYOUT_SPACING_PROPERTY = '--hotpads-static-map-layout-spacing';
const STATIC_MAP_HEADER_HEIGHT_PROPERTY = '--hotpads-static-map-header-height';
const STATIC_MAP_CONTENT_MAX_WIDTH_PROPERTY = '--hotpads-static-map-content-max-width';
const STATIC_MAP_PREVIEW_REQUEST_TIMEOUT_MS = 2500;
const STATIC_MAP_PREVIEW_PATH = '/api/static-map';

const previewImage = css({
  width: '100%',
  height: '100%',
  display: 'block',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  // The Maps Static API has no dark rendering, so dark mode blends the light image in CSS.
  _dark: { filter: 'invert(1) hue-rotate(180deg) opacity(0.5)' },
});

function buildBoundsParams(bounds: StaticMapBounds): URLSearchParams {
  return new URLSearchParams({
    cameraVersion: STATIC_MAP_CAMERA_VERSION,
    minLat: String(bounds.minLat),
    maxLat: String(bounds.maxLat),
    minLon: String(bounds.minLon),
    maxLon: String(bounds.maxLon),
  });
}

function getStaticMapPreviewRequestPrefix(bounds: StaticMapBounds): string {
  return `${STATIC_MAP_PREVIEW_PATH}?${buildBoundsParams(bounds).toString()}&width=`;
}

function buildStaticMapImageRequestScript(bounds: StaticMapBounds): string {
  // The script assembles the URL from a params object: a complete URL literal in the HTML gets
  // extracted and fetched by crawlers, which took a 400 on the dimensionless prefix (HP-8950).
  const boundsEntries = JSON.stringify(Object.fromEntries(buildBoundsParams(bounds)));
  return `if(!Number.isFinite(w)||!Number.isFinite(h)||w<=0||h<=0)return;const u=${JSON.stringify(STATIC_MAP_PREVIEW_PATH)}+'?'+new URLSearchParams(Object.assign(${boundsEntries},{width:w,height:h}));if(d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}===u)return;d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}=u;delete d.dataset.${STATIC_MAP_PREVIEW_LOADED_DATASET_KEY};delete d.dataset.${STATIC_MAP_PREVIEW_ERROR_DATASET_KEY};const i=new Image();i.fetchPriority='high';i.onload=()=>{if(d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}!==u)return;d.dataset.${STATIC_MAP_PREVIEW_LOADED_DATASET_KEY}='true';window.dispatchEvent(new Event(${JSON.stringify(STATIC_MAP_PREVIEW_LOAD_EVENT)}))};i.onerror=()=>{if(d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}!==u)return;d.dataset.${STATIC_MAP_PREVIEW_ERROR_DATASET_KEY}='true';window.dispatchEvent(new Event(${JSON.stringify(STATIC_MAP_PREVIEW_ERROR_EVENT)}))};i.src=u;d.style.setProperty(${JSON.stringify(STATIC_MAP_PREVIEW_PROPERTY)},'url("'+u+'")')`;
}

export function buildStaticMapPreviewHeadBootstrapScript(bounds: StaticMapBounds): string {
  const requestScript = buildStaticMapImageRequestScript(bounds);
  // Custom properties compute in their authored units (the spacing/header tokens
  // are rem), so scale by the root font size or the estimate diverges from the
  // panel-measured bootstrap and the preview downloads twice.
  return `(()=>{if(!window.matchMedia(${JSON.stringify(MIN_LG)}).matches)return;const d=document.documentElement;d.dataset.${STATIC_MAP_HEAD_BOOTSTRAP_DATASET_KEY}='true';const c=getComputedStyle(d),f=parseFloat(c.fontSize)||16,n=k=>{const v=c.getPropertyValue(k).trim(),p=parseFloat(v);return v.endsWith('rem')?p*f:p},s=n(${JSON.stringify(STATIC_MAP_LAYOUT_SPACING_PROPERTY)}),t=n(${JSON.stringify(STATIC_MAP_HEADER_HEIGHT_PROPERTY)}),x=n(${JSON.stringify(STATIC_MAP_CONTENT_MAX_WIDTH_PROPERTY)}),a=d.clientWidth-s*2,w=Math.round(a-s-Math.min(a/2,x)),h=Math.round(d.clientHeight-t-s);${requestScript}})()`;
}

export function buildStaticMapPreviewBootstrapScript(bounds: StaticMapBounds): string {
  const requestScript = buildStaticMapImageRequestScript(bounds);
  return `(()=>{const s=document.currentScript,m=s&&s.closest('#srp-map-panel');if(!m||!window.matchMedia(${JSON.stringify(MIN_LG)}).matches)return;const d=document.documentElement,r=m.getBoundingClientRect(),w=Math.round(r.width),h=Math.round(r.height);${requestScript}})()`;
}

function clearStaticMapPreviewState(): void {
  const root = document.documentElement;
  root.style.removeProperty(STATIC_MAP_PREVIEW_PROPERTY);
  delete root.dataset[STATIC_MAP_PREVIEW_LOADED_DATASET_KEY];
  delete root.dataset[STATIC_MAP_PREVIEW_ERROR_DATASET_KEY];
  delete root.dataset[STATIC_MAP_PREVIEW_URL_DATASET_KEY];
  delete root.dataset[STATIC_MAP_HEAD_BOOTSTRAP_DATASET_KEY];
}

/** Static snapshot started before hydration and kept aligned to the measured map panel. */
export function StaticMapPreview({
  bounds,
  onLoad,
  onError,
}: {
  bounds: StaticMapBounds;
  onLoad?: () => void;
  onError?: () => void;
}) {
  const requestPrefix = getStaticMapPreviewRequestPrefix(bounds);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const currentRequestUrl = root.dataset[STATIC_MAP_PREVIEW_URL_DATASET_KEY];
    const ownedRequestUrl = currentRequestUrl?.startsWith(requestPrefix) === true ? currentRequestUrl : undefined;
    const ownsCurrentRequest = () =>
      ownedRequestUrl !== undefined && root.dataset[STATIC_MAP_PREVIEW_URL_DATASET_KEY] === ownedRequestUrl;
    let timeoutHandle: number | undefined;
    const clearTimeoutHandle = () => {
      if (timeoutHandle === undefined) return;
      window.clearTimeout(timeoutHandle);
      timeoutHandle = undefined;
    };
    const handleLoad = () => {
      if (!ownsCurrentRequest()) return;
      clearTimeoutHandle();
      onLoad?.();
    };
    const handleError = () => {
      if (!ownsCurrentRequest()) return;
      clearTimeoutHandle();
      clearStaticMapPreviewState();
      onError?.();
    };

    window.addEventListener(STATIC_MAP_PREVIEW_LOAD_EVENT, handleLoad);
    window.addEventListener(STATIC_MAP_PREVIEW_ERROR_EVENT, handleError);
    if (!ownsCurrentRequest()) {
      clearStaticMapPreviewState();
      onError?.();
    } else if (root.dataset[STATIC_MAP_PREVIEW_LOADED_DATASET_KEY] === 'true') {
      handleLoad();
    } else if (root.dataset[STATIC_MAP_PREVIEW_ERROR_DATASET_KEY] === 'true') {
      handleError();
    } else {
      timeoutHandle = window.setTimeout(handleError, STATIC_MAP_PREVIEW_REQUEST_TIMEOUT_MS);
    }

    return () => {
      clearTimeoutHandle();
      window.removeEventListener(STATIC_MAP_PREVIEW_LOAD_EVENT, handleLoad);
      window.removeEventListener(STATIC_MAP_PREVIEW_ERROR_EVENT, handleError);
      if (ownsCurrentRequest()) clearStaticMapPreviewState();
    };
  }, [onError, onLoad, requestPrefix]);

  return (
    <>
      <div
        className={previewImage}
        style={{
          backgroundImage: 'var(--hotpads-static-map-preview-image, none)',
          backgroundSize: '100% 100%',
        }}
        data-static-map-preview
      />
      <script
        data-static-map-preview-bootstrap
        dangerouslySetInnerHTML={{ __html: buildStaticMapPreviewBootstrapScript(bounds) }}
      />
    </>
  );
}
